import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { HtmlOverlay3D } from '@asset-viz/objects/decor/HtmlOverlay3D'
import { useSurfaceSample } from '@asset-viz/objects/surfaces/util/useSurfaceSample'
import {
  HtmlOverlayAnchor,
  HtmlOverlayPriority,
} from '@asset-viz/utils/helpers'
import React from 'react'
import styled from 'styled-components'
import {
  GeckoComponentCoords,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
} from 'types'

import { useMarkupLayerProperties } from './useMarkupLayerProperties'

const StyledContainer = styled.div<{
  $opacity: number
  $isSelectable: boolean
}>`
  opacity: ${({ $opacity }) => $opacity};
  pointer-events: ${({ $isSelectable }) => ($isSelectable ? 'auto' : 'none')};
`

/**
 * A component used to standardize the handling (and appearance) of primitives
 * with an HTML component (such as labels, icons, etc.)
 * @param markupLayer         The markup layer this primitive belongs to
 * @param primitive           The primitive whose outline is being rendered
 * @param coords              The coords to render this overlay at
 * @param anchor              Where to anchor this element (top, left, etc.)
 * @param children            React elements to render at this point in space
 * @constructor
 */
export const PrimitiveHtmlOverlay3D: React.FC<{
  markupLayer: GeckoMarkupLayer
  primitive: GeckoMarkupPrimitive
  coords: GeckoComponentCoords
  anchor?: HtmlOverlayAnchor
  children: React.ReactNode
}> = ({ markupLayer, primitive, coords, anchor, children }) => {
  //----------------------------------------------------------------------------

  const { onClicked, onHovered } = useAssetVizInternal()
  const { position } = useSurfaceSample(coords)
  const { opacity, isEditingThisLayer } = useMarkupLayerProperties({
    markupLayer,
    primitive,
  })

  const mouseState = React.useMemo(
    () =>
      !position || !isEditingThisLayer
        ? undefined
        : {
            positionX: position.x,
            positionY: position.y,
            positionZ: position.z,
            componentX: coords.x.num,
            componentY: coords.y.num,
            componentWrapsX: false,
            componentWrapsY: false,
            componentBounds: undefined,
            componentDataFrame: undefined,
            componentModel: undefined,
            surfaceUserData: undefined,
            markupPrimitive: primitive,
            course: undefined,
            plate: undefined,
            wall: undefined,
            extrudingFeature: undefined,
            weld: undefined,
          },
    [position, isEditingThisLayer, coords, primitive]
  )

  const onClick = React.useCallback(
    (event: React.MouseEvent) =>
      mouseState &&
      onClicked({
        mouseX: event.clientX,
        mouseY: event.clientY,
        ...mouseState,
      }),
    [mouseState, onClicked]
  )

  const onHover = React.useCallback(
    (event: React.MouseEvent) =>
      mouseState &&
      onHovered({
        mouseX: event.clientX,
        mouseY: event.clientY,
        ...mouseState,
      }),
    [mouseState, onHovered]
  )

  //----------------------------------------------------------------------------

  return !position ? null : (
    <HtmlOverlay3D
      x={position.x}
      y={position.y}
      z={position.z}
      priority={HtmlOverlayPriority.Markup}
      anchor={anchor}>
      <StyledContainer
        $opacity={opacity}
        $isSelectable={opacity === 1 && isEditingThisLayer}
        onClick={onClick}
        onMouseEnter={onHover}>
        {children}
      </StyledContainer>
    </HtmlOverlay3D>
  )
}
